import { useEffect, useRef, type ReactNode } from 'react';
import Icon from './Icon';
import { useScrollLock } from './useScrollLock';
import { useSwipeDown } from './useSwipeDown';
import { useBackButton } from './useBackButton';

interface Props {
  open: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
}

/**
 * Модальное окно поверх текущего экрана.
 *
 * Заявка открывается здесь, а не отдельным экраном: человек листает ленту,
 * заглядывает в карточку и возвращается — при переходе на отдельный экран
 * лента каждый раз перезагружается и теряет позицию прокрутки, а он теряет
 * место, где остановился.
 *
 * На телефоне окно ведёт себя как «шторка»: тянется вниз пальцем и
 * закрывается броском. Это привычнее кнопки, потому что палец уже на экране.
 * Системная кнопка «назад» Telegram, пока окно открыто, тоже закрывает его.
 */
export default function Modal({ open, onClose, title, children }: Props) {
  const sheet = useRef<HTMLDivElement>(null);
  const { drag, bind } = useSwipeDown(sheet, onClose, {
    exclude: '.trackmap',
    active: open,
  });

  // Общее правило приложения: под открытым слоем страница не двигается.
  useScrollLock(open);

  // Пока окно открыто — системная кнопка «назад» закрывает его.
  useBackButton(open, onClose);

  useEffect(() => {
    if (!open) return;

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="modal"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      // Закрытие по клику на затемнение — только по нему самому: клик
      // внутри окна всплывает сюда же и закрывал бы окно при любом нажатии.
      onPointerDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="modal__sheet"
        ref={sheet}
        style={drag > 0 ? { transform: `translateY(${drag}px)` } : undefined}
        {...bind}
      >
        {/* Полоска-ручка: подсказывает, что окно тянется, ещё до того,
            как человек попробует. */}
        <div className="modal__grip" aria-hidden="true" />

        <div className="modal__head">
          {title && <strong className="modal__title">{title}</strong>}
          <button className="icon-btn modal__close" onClick={onClose} aria-label="Закрыть">
            <Icon name="close" size={18} />
          </button>
        </div>

        <div className="modal__body">{children}</div>
      </div>
    </div>
  );
}
